import type { MapDocument } from "../shared/types";
import {
  routingDocument,
  type RoutingFailure,
  type RoutingResult,
  type RoutingWorkerRequest,
  type RoutingWorkerResponse,
} from "./venueMapRoutingWorkerProtocol";

export interface RoutingWorkerPort {
  postMessage(request: RoutingWorkerRequest): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<RoutingWorkerResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
}

/** One active request and at most 64 cached legs (inside the planner) per map. */
export function createVenueMapRoutingClient(
  createWorker: () => RoutingWorkerPort,
  timeoutMs = 15_000,
) {
  let worker: RoutingWorkerPort | undefined;
  let snapshot: MapDocument | undefined;
  let disposed = false;
  let sequence = 0;
  let pending:
    { id: number; resolve: (result: RoutingResult) => void } | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;

  function finish(result: RoutingResult) {
    const request = pending;
    pending = undefined;
    clearTimeout(deadline);
    deadline = undefined;
    request?.resolve(result);
  }
  function terminate() {
    if (worker) {
      worker.onmessage = null;
      worker.onerror = null;
      worker.onmessageerror = null;
      worker.terminate();
    }
    worker = undefined;
    snapshot = undefined;
  }
  function cancel() {
    finish({ status: "cancelled" });
    terminate();
  }
  function fail(failure: RoutingFailure) {
    finish({ status: "unavailable", failure });
    terminate();
  }

  return {
    request(
      document: MapDocument,
      from: string,
      to: string,
    ): Promise<RoutingResult> {
      if (disposed) return Promise.resolve({ status: "cancelled" });
      // Termination interrupts expensive obsolete work rather than queueing
      // every requested destination behind it. A settled worker retains cache.
      if (pending || (snapshot && snapshot !== document)) cancel();
      return new Promise((resolve) => {
        const id = ++sequence;
        pending = { id, resolve };
        if (!worker) {
          try {
            const instance = createWorker();
            worker = instance;
            instance.onmessage = ({ data }) => {
              if (worker !== instance || data.id !== pending?.id) return;
              if (data.failed) {
                fail("routing_failed");
                return;
              }
              finish(
                data.points && data.segments
                  ? {
                      status: "ready",
                      points: data.points,
                      segments: data.segments,
                    }
                  : { status: "unavailable" },
              );
            };
            instance.onerror = (event) => {
              event.preventDefault();
              if (worker === instance) fail("routing_worker_unavailable");
            };
            instance.onmessageerror = () => {
              if (worker === instance) fail("routing_failed");
            };
          } catch {
            fail("routing_worker_unavailable");
            return;
          }
        }
        deadline = setTimeout(() => fail("routing_timeout"), timeoutMs);
        try {
          worker.postMessage({
            id,
            from,
            to,
            ...(snapshot !== document
              ? { document: routingDocument(document) }
              : {}),
          });
          snapshot = document;
        } catch {
          fail("routing_failed");
        }
      });
    },
    cancel,
    dispose() {
      disposed = true;
      cancel();
    },
  };
}
